import React from 'react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { formatFieldValue } from './adminFormatting';

// Long prose in a table cell is clamped by line count rather than by character
// count, so the cell shows as much as the column width allows instead of cutting
// at a fixed 60 characters. The full value is on hover, and in the expanded row.
export const ClampedText = ({ text, lines = 3, sx }) => {
  const value = (text || '').toString().trim();
  if (!value) return null;

  return (
    <Tooltip
      title={value}
      placement="top-start"
      enterDelay={300}
      slotProps={{
        tooltip: {
          sx: { maxWidth: 440, whiteSpace: 'pre-wrap', fontSize: '0.8rem', lineHeight: 1.5 },
        },
      }}
    >
      <Typography
        sx={{
          display: '-webkit-box',
          WebkitLineClamp: lines,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          cursor: 'help',
          ...sx,
        }}
      >
        {value}
      </Typography>
    </Tooltip>
  );
};

const FieldLabel = ({ children }) => (
  <Typography
    sx={{
      fontWeight: 900,
      fontSize: '0.7rem',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: '#6b6b6b',
    }}
  >
    {children}
  </Typography>
);

// Driven by the field schema the server ships with the summary payload, so a new
// intake-form field appears here and in the PDF without a change to this file.
export const RecordFields = ({ fields, record }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
      columnGap: 3,
      rowGap: 1.5,
    }}
  >
    {fields.map((field) => {
      const value = formatFieldValue(record?.[field.key], field.type);
      return (
        <Box
          key={field.key}
          sx={{ gridColumn: field.type === 'long' ? { xs: 'auto', md: '1 / -1' } : 'auto' }}
        >
          <FieldLabel>{field.label}</FieldLabel>
          {value ? (
            <Typography sx={{ color: '#1f1f1f', fontWeight: 600, whiteSpace: 'pre-wrap' }}>
              {value}
            </Typography>
          ) : (
            <Typography sx={{ color: '#9a9a9a', fontStyle: 'italic' }}>Not provided</Typography>
          )}
        </Box>
      );
    })}
  </Box>
);

export const AttachmentList = ({ attachments, emptyLabel = 'No files were uploaded.' }) => {
  if (!attachments?.length) {
    return <Typography sx={{ color: '#9a9a9a', fontStyle: 'italic' }}>{emptyLabel}</Typography>;
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
      {attachments.map((attachment) => (
        <Link
          key={attachment.key}
          href={attachment.url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.25,
            py: 0.75,
            borderRadius: 1.5,
            border: '1px solid rgba(80,0,0,0.2)',
            bgcolor: 'rgba(233,228,220,0.5)',
            color: '#500000',
            fontWeight: 800,
            textDecoration: 'none',
            '&:hover': { bgcolor: 'rgba(233,228,220,0.9)' },
          }}
          title={attachment.fileName || attachment.label}
        >
          <OpenInNewIcon sx={{ fontSize: '1rem' }} />
          {attachment.label}
          {attachment.fileName ? (
            <Typography component="span" sx={{ color: '#6b6b6b', fontWeight: 600, fontSize: '0.85rem' }}>
              {attachment.fileName}
            </Typography>
          ) : null}
        </Link>
      ))}
    </Box>
  );
};
